// src/data/officerNotes.js
//
// RULING 305.7 (Bob 2026-10-11: "A - agreed, but that must be made absolutely clear to the representatives - silly stupid
// comments can cause a lot of unnecessary damage - its on them to avoid it"). With 305.6 and rulings 147/148/151/152.
//
// An officer's note on a claim lives in claims/{claimId}/officerNotes, not in the claim's own `notes` (which the member
// reads). It is private to the union - the officers in scope and the administrator - while the union works the claim,
// the Local and General Chairperson's discussion between themselves included. It reaches the member at the EARLIEST of:
//   - the claim going back to her          (claim.returnedAt changes)            -> 'returned'
//   - the claim going to the carrier        (claim.presentedOn is first set)      -> 'presented'
//   - SURFACE_DAYS after it was written      (ruling 148's cap, kept)             -> 'thirty_days'
//   - the officer sharing it as he writes it                                       -> 'shared'
// No configuration can produce a never-surfaced note (147, negative control): the server sets visibleToMember; there is
// no client path that clears it, edits a note, or deletes one (firestore.rules, officerNotes).
//
// PURE. No Firestore, no clock: the caller passes the time. Shared with the server (scripts\sync-shared-modules.js).

export const SURFACE_DAYS = 30;
export const NOTE_MAX = 4000;
const DAY_MS = 24 * 60 * 60 * 1000;

export const SURFACE_WHY = Object.freeze({
  shared: 'shared by the officer when he wrote it',
  returned: 'the claim came back to the member',
  presented: 'the claim went to the carrier',
  thirty_days: SURFACE_DAYS + ' days after it was written',
  withdrawn: 'the member withdrew her submission, and the claim came back to her',
  released: 'the General Chairperson released the claim to the member',
});

// Ruling 307 (2026-10-12): carriage comes back to the claimant by her withdrawal or the General Chairperson's release
// (src\data\carriage.js carriageOf). Both are "the claim coming back to her" of 305.7(a). The acts live in
// claims/{id}/carriageActs, so the claim's own onUpdate never sees them; this reads one new act. Reason key, or null.
export function carriageReleaseReason(act) {
  const k = String((act && act.act) || '');
  return k === 'withdrawn' || k === 'released' ? k : null;
}

// What every officer reads above the box before he writes (Bob: "made absolutely clear to the representatives").
export const OFFICER_NOTE_WARNING =
  'The member WILL read this note. It stays between the union\'s officers only until the claim goes back to the member '
  + 'or to the carrier, or ' + SURFACE_DAYS + ' days after you write it - whichever comes first. Nothing written here is '
  + 'ever edited or deleted. Write nothing you would not say to the member yourself.';

// Milliseconds from a Firestore Timestamp, a Date, an ISO string or a number; null when there is none.
export function toMs(t) {
  if (t == null) return null;
  if (typeof t === 'number') return isFinite(t) ? t : null;
  if (t instanceof Date) return t.getTime();
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t === 'object' && typeof t.seconds === 'number') return t.seconds * 1000 + Math.floor((t.nanoseconds || 0) / 1e6);
  const ms = Date.parse(String(t));
  return isNaN(ms) ? null : ms;
}

// When the 30-day cap brings a note to the member, in ms (null if the note has no time yet).
export function surfaceDueMs(note) {
  const at = toMs(note && note.addedAt);
  return at == null ? null : at + SURFACE_DAYS * DAY_MS;
}

// Is a hidden note due to the member by the cap at nowMs?
export function dueByCap(note, nowMs) {
  if (!note || note.visibleToMember === true) return false;
  const due = surfaceDueMs(note);
  return due != null && nowMs >= due;
}

// The claim moved out of the union between two versions of it: back to the member, or to the carrier. Returns the
// reason key, or null. `before`/`after` are the claim's data before and after one write.
export function releaseReason(before, after) {
  const b = before || {}, a = after || {};
  if (a.returnedAt && String(a.returnedAt) !== String(b.returnedAt || '')) return 'returned';
  if (a.presentedOn && !b.presentedOn) return 'presented';
  return null;
}

// The record a new officer note is written as. serverTime is the sentinel the caller passes (serverTimestamp()), so the
// rule can pin addedAt to the server's clock. Returns { ok, errors, doc }.
export function newOfficerNote(user, text, shareNow, serverTime) {
  const t = String(text || '').trim();
  const errors = [];
  // A tester working his EXAMPLE claim as its Local Chairperson (testTerritory.viewerFor) writes with his real token role -
  // the rules pin role == the token's.
  const role = String((user && (user.testerAsLC ? user.tokenRole : user.role)) || '');
  if (!user || !user.uid) errors.push('Not signed in.');
  if (!['local_chair', 'general_chair', 'admin'].includes(role) && !(user && user.testerAsLC && role === 'tester')) errors.push('Only an officer writes an officer note.');
  if (!t) errors.push('The note is empty.');
  if (t.length > NOTE_MAX) errors.push('A note is limited to ' + NOTE_MAX + ' characters.');
  if (errors.length) return { ok: false, errors, doc: null };
  return {
    ok: true, errors: [],
    doc: {
      text: t,
      addedBy: String(user.name || user.email || 'Unknown').slice(0, 200),
      addedByUid: user.uid,
      role,
      addedAt: serverTime,
      visibleToMember: !!shareNow,
      surfacedAt: shareNow ? serverTime : null,
      surfacedWhy: shareNow ? 'shared' : null,
    },
  };
}

// The line an officer sees under a note: who can read it, and from when.
export function visibilityLine(note) {
  if (note && note.visibleToMember) {
    const at = toMs(note.surfacedAt);
    return 'The member can read this' + (at ? ' since ' + new Date(at).toISOString().slice(0, 10) : '') + ' - '
      + (SURFACE_WHY[note.surfacedWhy] || 'released') + '.';
  }
  const due = surfaceDueMs(note);
  return 'Officers only for now. The member reads it when the claim goes back to her or to the carrier'
    + (due ? ', and no later than ' + new Date(due).toISOString().slice(0, 10) : '') + '.';
}
